'use no memo';

import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { HStack, VStack } from '@/components/Layout';
import { Text } from '@/components/Typography/Text';
import type { DeviceGenre } from '@/constants/device';
import { LabelDevice } from '@/features/device/components/LabelDevice/LabelDevice';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import useValueToLabelCache from '@/hooks/useValueToLabelCache';
import { defaultNS, useTranslation } from '@/lib/i18n';
import Combobox from '../index';
import type { ComboboxOptionRenderer } from '../types';
import { useDeviceCatalogsForSelect, type UseDeviceCatalogsForSelect } from './-query';
import type { DeviceCatalogOption } from './-query/selector';

/**
 * DeviceCatalog情報を表す型
 */
export type DeviceCatalogInfo = Readonly<{
  id: string;
  name: string;
  catalogCode: string;
  deviceType: string;
  avatarUrl?: string;
}>;
interface DeviceCatalogComboboxProps {
  onSelect: (catalog: DeviceCatalogInfo) => void;
  onClose: () => void;
  /**
   * カスタム useQuery Hook（依存性注入）
   * Individual 画面など、異なる API エンドポイントを使用する場合に指定
   * 未指定の場合は Operator 用のデフォルト Hook を使用
   */
  useQuery?: UseDeviceCatalogsForSelect;
  /** 紐付け対象デバイスのジャンル。候補を同一ジャンルへ限定する。 */
  genre?: DeviceGenre;
  /** ジャンル未確定時など、候補queryを実行しない場合にfalseを渡す。 */
  queryEnabled?: boolean;
}
const renderDeviceCatalogOption: ComboboxOptionRenderer<DeviceCatalogOption> = ({
  option
}) => <HStack className="items-center gap-3">
    <LabelDevice name={option.label} avatarUrl={option.avatarUrl} genre={option.deviceType as DeviceGenre} />
    <VStack>
      <Text variant="labelSmall" className="text-muted-foreground">
        {option.catalogCode}
      </Text>
    </VStack>
  </HStack>;
export default function DeviceCatalogCombobox({
  onSelect,
  onClose,
  useQuery = useDeviceCatalogsForSelect,
  genre,
  queryEnabled = true
}: DeviceCatalogComboboxProps) {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'component.Combobox.deviceCatalogCombobox'
  });
  const queryClient = useQueryClient();
  const [searchText, setSearchText] = useState('');
  const [value, setValue] = useState<string | null>(null);
  const valueToLabelCache = useValueToLabelCache();
  const {
    data: options = [],
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching
  } = useQuery(searchText, genre, queryEnabled);
  const handleValueChange = (newValue: string | null) => {
    if (!newValue) return;
    setValue(newValue);
    const selectedOption = options.find(option => option.value === newValue);
    if (!selectedOption) return;
    const catalogInfo: DeviceCatalogInfo = {
      id: selectedOption.id,
      name: selectedOption.label,
      catalogCode: selectedOption.catalogCode,
      deviceType: selectedOption.deviceType,
      avatarUrl: selectedOption.avatarUrl
    };

    // 選択したカタログ情報をキャッシュに保存
    // useDeviceCatalogQuery で再取得せずにキャッシュから取得できるようにする
    queryClient.setQueryData(['deviceCatalog', selectedOption.id], catalogInfo);
    onSelect(catalogInfo);
    onClose();
  };
  const handleScrollToEnd = () => {
    if (hasNextPage && !isFetching) {
      fetchNextPage();
    }
  };
  return <Combobox type="single" value={value} options={options} isFetching={isLoading || isFetchingNextPage} onValueChange={handleValueChange} searchPlaceholder={t('searchCatalogPlaceholder')} onSearchChange={useDebouncedCallback(text => setSearchText(text), [])} valueToLabelCache={valueToLabelCache} renderOption={renderDeviceCatalogOption} onScrollToEnd={handleScrollToEnd} />;
}